import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { injectHotkeyManagerService } from '@libs/shared/hotkey/hotkey-manager.service';
import { InputClearButtonComponent } from '@libs/shared/ui/form-fields/input-clear-button/input-clear-button.component';
import { allAllowInElements } from '@libs/shared/utils/const/shortcut';
import { ExpertWorkspaceReferralInvitationDeepLinkDetailService } from '@sprint-expert/app/modules/expert-workspace-referral-invitation-deep-link-detail/expert-workspace-referral-invitation-deep-link-detail.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'app-expert-workspace-referral-invitation-deep-link-detail',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    InputClearButtonComponent,
    NgxMaskDirective,
  ],
  template: `
    @if (vm.formGroup) {
      <div
        class="flex w-full flex-col justify-between gap-6 rounded-lg md:p-10">
        <div class="mb-4 flex flex-row items-start justify-between">
          <div class="flex w-full flex-col">
            <span class="text-4xl font-bold text-gray-800 dark:text-gray-100"
              >친구 초대하기</span
            >
            <span
              class="mt-3 text-lg leading-relaxed text-gray-600 dark:text-gray-400">
              초대받은 친구가 첫 결제를 완료하면,
              <strong
                class="text-primary-600 dark:text-primary-400 font-semibold"
                >친구와 코치님 모두에게 1개월 무료 이용권(크레딧)</strong
              >이 지급됩니다!
            </span>
          </div>
          <button
            mat-icon-button
            (click)="byClose.emit()"
            tabindex="-1"
            matTooltip="닫기"
            matTooltipPosition="above"
            class="-mr-3 -mt-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <mat-icon class="h-7 w-7" svgIcon="heroicons_outline:x-mark" />
          </button>
        </div>

        <div
          class="flex flex-col items-center justify-center gap-5 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 dark:border-gray-600 dark:bg-gray-800">
          <div class="flex flex-col items-center justify-center text-center">
            <p class="text-lg text-gray-500 dark:text-gray-400">
              <span class="font-semibold text-gray-700 dark:text-gray-200">{{
                vm.currentExpertWorkspace$$()?.name ?? '내 센터'
              }}</span>
              의 초대 코드 ✨
            </p>
            <p
              class="text-primary-600 dark:text-primary-400 mt-3 cursor-pointer text-4xl font-bold"
              matTooltip="클릭해서 복사"
              (click)="vm.copyExpertWorkspaceCode()">
              &#64;{{ vm.currentExpertWorkspace$$()?.code }}
            </p>
            <p class="mt-3 text-base text-gray-500 dark:text-gray-400">
              친구가 신규 가입 및 첫 결제 시 위 코드를 입력하면 혜택이
              적용됩니다.
            </p>
          </div>
          <div class="mt-3 flex flex-row items-center justify-center">
            <button
              mat-stroked-button
              color="primary"
              tabindex="-1"
              class="flex items-center gap-2 px-6 py-3 text-lg"
              (click)="vm.copyInvitationTextToClipboard()">
              <mat-icon class="icon-size-6" svgIcon="mat_outline:link" />
              <span>초대 메시지 복사</span>
            </button>
          </div>
        </div>

        <div class="my-4 flex items-center justify-center">
          <span class="px-3 text-lg text-gray-400 dark:text-gray-500"
            >또는</span
          >
        </div>

        <div class="flex flex-col gap-5">
          <p class="text-center text-lg text-gray-600 dark:text-gray-400">
            아래 연락처로 혜택 정보가 포함된 초대 메시지를 바로 보낼 수 있어요.
          </p>
          <form
            [formGroup]="vm.formGroup"
            (keydown.enter)="$event.preventDefault()">
            <mat-form-field appearance="outline" class="w-full text-lg">
              <mat-label>받는 사람 연락처</mat-label>
              <input
                matInput
                required
                autocomplete="tel"
                inputmode="tel"
                mask="000-0000-0000"
                placeholder="010-0000-0000"
                type="text"
                formControlName="phoneNumber"
                class="text-lg" />
              <app-input-clear-button
                matSuffix
                [control]="vm.formGroup.controls.phoneNumber" />
              @if (vm.formGroup.controls.phoneNumber.hasError('required')) {
                <mat-error class="text-base"
                  >전화번호는 필수 입력 항목입니다.
                </mat-error>
              }
              @if (
                vm.formGroup.controls.phoneNumber.hasError('invalidPhoneNumber')
              ) {
                <mat-error class="text-base"
                  >올바른 전화번호 형식이 아닙니다 (예: 010-1234-5678).
                </mat-error>
              }
            </mat-form-field>
          </form>
          <button
            class="flex w-full items-center justify-center gap-2 rounded-md px-6 py-4 text-lg font-medium"
            tabindex="-1"
            [disabled]="vm.formGroup.invalid || !vm.invitationText$$()"
            mat-flat-button
            color="primary"
            (click)="
              vm.sendExpertWorkspaceReferralInvitationDeepLinkDetailSms()
            ">
            <mat-icon class="icon-size-6" svgIcon="feather:send" />
            <span>초대 문자 보내기</span>
          </button>
        </div>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }

      /* 필요하다면 추가적인 스타일링 */
    `,
  ],
  providers: [
    ExpertWorkspaceReferralInvitationDeepLinkDetailService,
    provideNgxMask(),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpertWorkspaceReferralInvitationDeepLinkDetailComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly hotkeyManager = injectHotkeyManagerService();
  readonly vm = inject(ExpertWorkspaceReferralInvitationDeepLinkDetailService);

  @Output() byClose = new EventEmitter<void>();

  ngOnInit(): void {
    this.vm.enter();
  }

  ngAfterViewInit(): void {
    this.hotkeyManager.activateHotkeysForComponent(this.constructor.name, [
      {
        keys: 'escape',
        description: '닫기',
        allowIn: allAllowInElements,
        action: () => {
          this.byClose.emit();
        },
      },
    ]);
  }

  ngOnDestroy(): void {
    this.hotkeyManager.deactivateHotkeys(this.constructor.name);
  }
}
